// Emlak CMS — SSS (FAQ) types — aligned with real backend (S13, Blok 1 §5a).
//
// Backend uses a flat shape: { question, answer, categoryId, order, helpfulYesCount,
// helpfulNoCount, status (int enum), publishedAt, createdAt, updatedAt }.
//
// Backend `BaseStatus` enum is numeric (shared with Article/Page):
//   Inactive=0, Active=1, Deleted=2, Archived=3, Draft=4, InReview=5,
//   Approved=6, Published=7, Rejected=8
//
// SSS runs the SAME six-state review workflow as Article/Page (submit-review →
// approve/reject → publish → archive), so it exposes the granular union and the
// `toContentStatus()` collapse used by the shared 4-state StatusBadge.

import { useTranslation } from 'react-i18next';
import type { ContentStatus } from '@shared/constants/statuses';

export type SssStatus =
  | 'DRAFT'
  | 'IN_REVIEW'
  | 'APPROVED'
  | 'PUBLISHED'
  | 'ARCHIVED'
  | 'REJECTED'
  | 'DELETED';

/** Statuses surfaced in the list-page status filter. */
export const SSS_FILTER_STATUSES: SssStatus[] = [
  'DRAFT',
  'IN_REVIEW',
  'APPROVED',
  'PUBLISHED',
  'ARCHIVED',
  'REJECTED',
];

/** Hook resolving the granular SSS status label via the `sss` namespace (status.*). */
export function useSssStatusLabel(): (status: SssStatus) => string {
  const { t } = useTranslation('sss');
  const td = t as unknown as (key: string) => string;
  return (status) => td(`status.${status}`);
}

const STATUS_TO_NUM: Record<SssStatus, number> = {
  DELETED: 2,
  ARCHIVED: 3,
  DRAFT: 4,
  IN_REVIEW: 5,
  APPROVED: 6,
  PUBLISHED: 7,
  REJECTED: 8,
};

const NUM_TO_STATUS: Record<number, SssStatus> = {
  2: 'DELETED',
  3: 'ARCHIVED',
  4: 'DRAFT',
  5: 'IN_REVIEW',
  6: 'APPROVED',
  7: 'PUBLISHED',
  8: 'REJECTED',
};

const STRING_TO_STATUS: Record<string, SssStatus> = {
  Deleted: 'DELETED',
  Archived: 'ARCHIVED',
  Draft: 'DRAFT',
  InReview: 'IN_REVIEW',
  Approved: 'APPROVED',
  Published: 'PUBLISHED',
  Rejected: 'REJECTED',
};

/** K-13 #19 — FE durumu → BE `BaseStatus` adı (`?status=` query paramı). */
const STATUS_TO_BACKEND: Record<SssStatus, string> = {
  DELETED: 'Deleted',
  ARCHIVED: 'Archived',
  DRAFT: 'Draft',
  IN_REVIEW: 'InReview',
  APPROVED: 'Approved',
  PUBLISHED: 'Published',
  REJECTED: 'Rejected',
};

export function sssStatusToBackend(s: SssStatus): string {
  return STATUS_TO_BACKEND[s];
}

/** Backend status (numeric BaseStatus or PascalCase string) → FE granular status. */
export function decodeSssStatus(raw: unknown): SssStatus {
  if (typeof raw === 'number') return NUM_TO_STATUS[raw] ?? 'DRAFT';
  if (typeof raw === 'string') return STRING_TO_STATUS[raw] ?? 'DRAFT';
  return 'DRAFT';
}

export function sssStatusToNumber(s: SssStatus): number {
  return STATUS_TO_NUM[s];
}

/** Collapse onto the 4-state `ContentStatus` used by the shared StatusBadge. */
export function toContentStatus(s: SssStatus): ContentStatus {
  switch (s) {
    case 'DRAFT':
    case 'REJECTED':
      return 'DRAFT';
    case 'IN_REVIEW':
    case 'APPROVED':
      return 'REVIEW';
    case 'PUBLISHED':
      return 'PUBLISHED';
    case 'ARCHIVED':
    case 'DELETED':
      return 'ARCHIVED';
  }
}

export interface SssVersionSnapshot {
  id: string;
  faqId: string;
  versionNumber: number;
  createdAt: string;
  authorId: string | null;
  authorName: string;
  note?: string;
  snapshot: {
    question: string;
    answer: string;
    categoryId: string | null;
    order: number;
  };
}

export interface SssFaq {
  id: string;
  question: string;
  /** Detail endpoint populates this; list endpoint may omit. */
  answer: string;
  categoryId: string | null;
  order: number;
  helpfulYesCount: number;
  helpfulNoCount: number;
  status: SssStatus;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // --- İçerik i18n (MOD-09) ---
  languageId: string;
  /** Çeviri grubu — aynı groupId'yi paylaşan SSS'ler birbirinin varyantıdır. */
  groupId: string;
  /** Bu SSS'in grubunda mevcut dil KODLARI (örn. ["tr","en"]). */
  variantLanguageCodes: string[];
}

export type SssSortBy = 'order' | 'recent' | 'helpful';

export interface SssListQuery {
  page?: number;
  pageSize?: number;
  /** Free-text search — mapped to backend `search`. Legacy alias: `q`. */
  search?: string;
  q?: string;
  categoryId?: string;
  /** Backend filter — when true, restricts to published items. */
  publishedOnly?: boolean;
  sortBy?: SssSortBy;
  /** Legacy alias from MSW era — accepted but only used to derive `publishedOnly`. */
  status?: SssStatus | 'ALL';
  /** İçerik i18n — belirli bir dile göre süz. */
  languageId?: string;
}

export interface SssListResponse {
  items: SssFaq[];
  /** Total count from backend pagination envelope (alias for `pagination.totalCount`). */
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateSssInput {
  question: string;
  answer: string;
  categoryId: string | null;
  /** Optional — backend auto-assigns MAX+1 when omitted. */
  order?: number | null;
  // --- İçerik i18n (create) — languageId yoksa BE varsayılan dili kullanır;
  // groupId yalnız varyant eklerken gönderilir. ---
  languageId?: string | null;
  groupId?: string | null;
}

export interface UpdateSssInput {
  question: string;
  answer: string;
  categoryId: string | null;
  order?: number | null;
  changeNote?: string;
}

export interface ReorderEntry {
  id: string;
  order: number;
}

// ── Analytics ─────────────────────────────────────────────────────────────

export interface SssAnalyticsRow {
  id: string;
  question: string;
  helpfulYesCount: number;
  helpfulNoCount: number;
  totalVotes: number;
  yesRatio: number;
}

export interface SssAnalyticsData {
  totalItems: number;
  totalPublished: number;
  totalYes: number;
  totalNo: number;
  overallRatio: number;
  mostHelpful: SssAnalyticsRow[];
  leastHelpful: SssAnalyticsRow[];
  mostVoted: SssAnalyticsRow[];

  // ── Backward-compat fields for legacy consumers (dashboard widgets). ─
  /** Alias for `totalItems`. */
  total: number;
  /** Approx total votes (yes+no) — used by dashboard "görüntülenme" widget. */
  totalViews: number;
}
